/**
 * usePOSReconciliation Hook
 * 
 * Provides aggregated view of POS offline sync status across branches and terminals.
 * Used by managers to monitor and recover offline POS operations.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  listQueueItems, 
  getQueueStats, 
  retryQueueItem,
  discardQueueItem,
  replayPendingQueue,
  updateQueueItem
} from '@/lib/offline';
import { 
  isPOSAction, 
  getPOSModule, 
  getPOSActionDisplayName,
  POS_ACTION_TYPES 
} from '@/lib/offline/posPolicies';

/**
 * Aggregate sync status by terminal
 */
function aggregateByTerminal(items) {
  const terminals = new Map();
  
  items.forEach(item => {
    const terminalId = item.terminalId || item.payload?.terminal_id || 'unknown';
    const branchId = item.branchId || item.payload?.branch_id || item.merchantId || 'unknown';
    
    if (!terminals.has(terminalId)) {
      terminals.set(terminalId, {
        terminalId,
        branchId,
        pending: 0,
        syncing: 0,
        failed: 0,
        conflict: 0,
        completed: 0,
        totalAmount: 0,
        lastActivity: null,
        items: [],
        oldestPending: null,
        hasOrders: false,
        hasPayments: false
      });
    }
    
    const terminal = terminals.get(terminalId);
    terminal[item.status]++;
    terminal.items.push(item);
    
    // Track amounts for order/payment items
    if (item.payload?.total || item.payload?.amount) {
      terminal.totalAmount += item.payload.total || item.payload.amount || 0;
    }
    
    // Track last activity
    if (!terminal.lastActivity || item.updatedAt > terminal.lastActivity) {
      terminal.lastActivity = item.updatedAt;
    }
    
    // Track oldest pending
    if (item.status === 'pending' || item.status === 'failed') {
      if (!terminal.oldestPending || item.createdAt < terminal.oldestPending) {
        terminal.oldestPending = item.createdAt;
      }
    }
    
    // Track item types
    if (item.type.includes('ORDER')) terminal.hasOrders = true;
    if (item.type.includes('PAYMENT')) terminal.hasPayments = true;
  });
  
  return Array.from(terminals.values());
}

/**
 * Aggregate sync status by branch
 */
function aggregateByBranch(items) {
  const branches = new Map();
  
  items.forEach(item => {
    const branchId = item.branchId || item.payload?.branch_id || item.merchantId || 'unknown';
    
    if (!branches.has(branchId)) {
      branches.set(branchId, {
        branchId,
        branchName: item.branchName || `Branch ${branchId}`,
        terminals: new Set(),
        pending: 0,
        syncing: 0,
        failed: 0,
        conflict: 0,
        completed: 0,
        totalAmount: 0,
        lastActivity: null,
        items: []
      });
    }
    
    const branch = branches.get(branchId);
    branch[item.status]++;
    branch.items.push(item);
    
    const terminalId = item.terminalId || item.payload?.terminal_id || 'unknown';
    branch.terminals.add(terminalId);
    
    if (item.payload?.total || item.payload?.amount) {
      branch.totalAmount += item.payload.total || item.payload.amount || 0;
    }
    
    if (!branch.lastActivity || item.updatedAt > branch.lastActivity) {
      branch.lastActivity = item.updatedAt;
    }
  });
  
  return Array.from(branches.values()).map(b => ({
    ...b,
    terminals: b.terminals.size,
    terminalList: Array.from(b.terminals)
  }));
}

/**
 * Main hook for POS Reconciliation Dashboard
 */
export function usePOSReconciliation(options = {}) {
  const { 
    autoRefresh = true, 
    refreshInterval = 10000,
    branchId = null,
    terminalId = null 
  } = options;
  
  // State
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filters, setFilters] = useState({
    branch: branchId,
    terminal: terminalId,
    status: null, // pending, failed, conflict
    actionType: null,
    dateFrom: null,
    dateTo: null
  });

  // Refresh data
  const refreshData = useCallback(async () => {
    try {
      const items = await listQueueItems();
      const posItems = items.filter(isPOSAction);
      
      // Enrich items with display info
      const enrichedItems = posItems.map(item => ({
        ...item,
        moduleName: getPOSModule(item.type),
        actionDisplayName: getPOSActionDisplayName(item.type),
        terminalId: item.terminalId || item.payload?.terminal_id || 'T001',
        branchId: item.branchId || item.payload?.branch_id || item.merchantId
      }));
      
      setAllItems(enrichedItems);
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('[usePOSReconciliation] Failed to refresh:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and auto-refresh
  useEffect(() => {
    refreshData();
    
    if (autoRefresh) {
      const interval = setInterval(refreshData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshData, autoRefresh, refreshInterval]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      if (filters.branch && item.branchId !== filters.branch) return false;
      if (filters.terminal && item.terminalId !== filters.terminal) return false;
      if (filters.status && item.status !== filters.status) return false;
      if (filters.actionType && item.type !== filters.actionType) return false;
      if (filters.dateFrom && item.createdAt < new Date(filters.dateFrom).getTime()) return false;
      if (filters.dateTo && item.createdAt > new Date(filters.dateTo).getTime()) return false;
      return true;
    });
  }, [allItems, filters]);

  // Aggregations
  const terminalStats = useMemo(() => aggregateByTerminal(filteredItems), [filteredItems]);
  const branchStats = useMemo(() => aggregateByBranch(filteredItems), [filteredItems]);

  // Summary counts
  const summaryCounts = useMemo(() => {
    const counts = {
      total: filteredItems.length,
      pending: 0,
      syncing: 0,
      failed: 0,
      conflict: 0,
      completed: 0,
      orders: 0,
      payments: 0,
      totalAmount: 0,
      needsAttention: 0,
      terminals: new Set(),
      branches: new Set()
    };
    
    filteredItems.forEach(item => {
      counts[item.status]++;
      counts.terminals.add(item.terminalId);
      counts.branches.add(item.branchId);
      
      if (item.type.includes('ORDER')) counts.orders++;
      if (item.type.includes('PAYMENT')) counts.payments++;
      
      if (item.payload?.total || item.payload?.amount) {
        counts.totalAmount += item.payload.total || item.payload.amount || 0;
      }
    });
    
    counts.needsAttention = counts.failed + counts.conflict;
    counts.terminalCount = counts.terminals.size;
    counts.branchCount = counts.branches.size;
    
    return counts;
  }, [filteredItems]);

  // Available filter options
  const filterOptions = useMemo(() => {
    const branches = [...new Set(allItems.map(i => i.branchId))];
    const terminals = [...new Set(allItems.map(i => i.terminalId))];
    const actionTypes = [...new Set(allItems.map(i => i.type))];
    
    return { branches, terminals, actionTypes };
  }, [allItems]);

  // Items by status
  const itemsByStatus = useMemo(() => ({
    pending: filteredItems.filter(i => i.status === 'pending' || i.status === 'syncing'),
    failed: filteredItems.filter(i => i.status === 'failed'),
    conflict: filteredItems.filter(i => i.status === 'conflict'),
    completed: filteredItems.filter(i => i.status === 'completed').slice(0, 50)
  }), [filteredItems]);

  // Actions

  /**
   * Retry a single item
   */
  const retryItem = useCallback(async (itemId) => {
    try {
      await retryQueueItem(itemId);
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Discard a single item
   */
  const discardItem = useCallback(async (itemId) => {
    try {
      await discardQueueItem(itemId);
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Retry all failed items for a terminal
   */
  const retryTerminal = useCallback(async (targetTerminalId) => {
    try {
      const terminalItems = allItems.filter(
        i => i.terminalId === targetTerminalId && (i.status === 'failed' || i.status === 'conflict')
      );
      
      await Promise.all(terminalItems.map(i => retryQueueItem(i.id)));
      await refreshData();
      
      return { success: true, count: terminalItems.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [allItems, refreshData]);

  /**
   * Retry all failed items for a branch
   */
  const retryBranch = useCallback(async (targetBranchId) => {
    try {
      const branchItems = allItems.filter(
        i => i.branchId === targetBranchId && (i.status === 'failed' || i.status === 'conflict')
      );
      
      await Promise.all(branchItems.map(i => retryQueueItem(i.id)));
      await refreshData();
      
      return { success: true, count: branchItems.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [allItems, refreshData]);

  /**
   * Retry all failed items
   */
  const retryAll = useCallback(async () => {
    try {
      const failedItems = allItems.filter(i => i.status === 'failed' || i.status === 'conflict');
      await Promise.all(failedItems.map(i => retryQueueItem(i.id)));
      await refreshData();
      return { success: true, count: failedItems.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [allItems, refreshData]);

  /**
   * Force sync now
   */
  const syncNow = useCallback(async () => {
    try {
      const result = await replayPendingQueue({ limit: 100 });
      await refreshData();
      return { success: true, result };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Mark item as reviewed
   */
  const markReviewed = useCallback(async (itemId, reviewer, note) => {
    try {
      await updateQueueItem(itemId, {
        manualResolutionStatus: 'reviewed',
        reviewedBy: reviewer,
        reviewedAt: Date.now(),
        managerNote: note
      });
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Add manager note
   */
  const addNote = useCallback(async (itemId, note, actor) => {
    try {
      await updateQueueItem(itemId, {
        managerNote: note,
        noteAddedBy: actor,
        noteAddedAt: Date.now()
      });
      await refreshData();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  return {
    // Data
    allItems,
    filteredItems,
    terminalStats,
    branchStats,
    summaryCounts,
    itemsByStatus,
    filterOptions,
    
    // State
    loading,
    lastRefresh,
    filters,
    setFilters,
    
    // Actions
    refreshData,
    retryItem,
    discardItem,
    retryTerminal,
    retryBranch,
    retryAll,
    syncNow,
    markReviewed,
    addNote
  };
}

export default usePOSReconciliation;
